import getCountries, { getAvailableCountries } from "./getCountries";
import getStates from "./getStates";
import useDebounse from "./useDebounce";
import useWindowDimensions from "./useWindowDimensions";
import exportToCSV from "./csvExport";

const emailIsValid = (email: unknown): boolean => {
    if (typeof email !== 'string') {
        return false;
    } else {
        const exp = new RegExp(
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        );
        return exp.test(email);
    }
};

const passwordIsValid = (password: unknown): boolean => {
    if (typeof password !== 'string') {
        return false;
    } else {
        const exp = new RegExp(/(?=(.*[0-9]))(?=.*[\!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?])(?=.*[a-z])(?=(.*[A-Z]))(?=(.*)).{8,}/);
        return exp.test(password);
    }
};

const phoneNumberIsValid = (value: unknown): boolean => {
    if (typeof value !== 'string') {
        return false;
    } else {
        const regex = new RegExp(/^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/);
        if (regex.test(value)) {
            return true;
        } else {
            return false;
        }
    }
};

const passwordAreEqual = (password: unknown, confirmPassword: unknown): boolean => {
    if (typeof password !== 'string' || typeof confirmPassword !== 'string') {
        return false;
    } else {
        return confirmPassword === password;
    }
};

const zipCodeIsValid = (zip: unknown, country: string): boolean => {
    if(typeof zip !== 'string') {
        return false;
    }

    const trimmedZip = zip.trim();

    const usZipPattern = /^\d{5}$/;
    const canadianZipPattern = /^[A-Z]\d[A-Z] \d[A-Z]\d$/i;

    if(country === 'US' && usZipPattern.test(trimmedZip)) {
        return true;
    }
    if(country === 'CA' && canadianZipPattern.test(trimmedZip)) {
        return true;
    }

    return false;
};

const dateIsValid = (dob: unknown): boolean => {
    if (typeof dob !== 'string') {
        return false;
    } else {
        const date = new Date(dob);
        return !isNaN(date.getTime());
    }
};

const urlIsValid = (url: unknown): boolean => {
    if (typeof url !== 'string') {
        return false;
    }

    try {
        // The URL constructor throws an error if the URL is invalid
        new URL(url);
        return true;
    } catch {
        return false;
    }
};


const formatPhoneNumber = (phoneNumber: unknown, noSpaceFormat?: boolean ): string => {
    if (typeof phoneNumber !== 'string') {
        return 'Invalid number';
    } else {
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            const intlCode = match[1] ? '+1 ' : '';
            if(noSpaceFormat) return [intlCode, '(', match[2], ')-', match[3], '-', match[4]].join(''); 
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return 'Invalid number';
    }
};

const formatLocation = (
    addressline1: string,
    addressLine2 = '',
    city: string,
    state: string,
    country: string,
    zip: string,
) => {

    if(addressline1.length>1){
       return [addressline1, addressLine2, city, state, country, zip].join(', ').replace(/(, ){2,}/g, ', ')
    } 
    else{
        return [city, state, country, zip].join(', ').replace(/(, ){2,}/g, ', ')
    }
    
};

const formatDate = (date: string | Date):string => {
    return new Date(date).toLocaleDateString('en-US');
}

const formatDateOnly = (date: string | Date): string => {
    if(date) {
        const datePart = date.toString().split('T')[0];
        if(datePart) {
            const resp = datePart.split('-');
            return `${resp[1]}/${resp[2]}/${resp[0]}`
        }
        return date.toString();
    }
    return "invalid date";
}

const FormatDateInputValue = (date: string | Date | null | undefined):string | undefined=> {
    if(!date || date.toString().length===0) return undefined; 
    date = new Date(date);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());    
    date =  date.toISOString().substring(0, 10);
    return date;
}

const setCookie=(cname: string, expireAfterMonths: number, value: string, path?:string) => {
    const expiryMonth = new Date(new Date().getMonth()+expireAfterMonths).toUTCString();
    document.cookie = `${cname}=${value}; expires=${expiryMonth}; path=${path}`;
}

const getCookie=(cname: string):string=> {
    const name = cname + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
        c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
        }
    }
    return "";

}

const getUserRoles = (): { value: string; label: string }[] => {
    return [
        {
            label: 'Admin',
            value: 'Admin',
        },
        {
            label: 'Manager',
            value: 'Manager',
        },
        {
            label: 'Synner',
            value: 'Synner',
        },        
        {
            label: 'Trainee',
            value: 'Trainee',
        },
    ];
};

const endDateIsValid = (startDate: unknown, endDate: unknown): boolean => {
    if (typeof startDate !== 'string' || typeof endDate !== 'string') {
        return false;
    } else {
        const newStartDate = new Date(startDate);
        const newEndDate = new Date(endDate);

        return newEndDate >= newStartDate;
    }
};

const amountIsValid = (amount: unknown): boolean => {
    if (typeof amount !== 'string' && typeof amount !== 'number') {
        return false;
    } else {
        const regex = new RegExp(/^\d+(?:[.,]\d+)*$/);
        if (regex.test(amount.toString())) {
            return true;
        } else {
            return false;
        }
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getListFromEnum(enumvalues: string | any[]): {label: string, value: string | number}[]{
    const resp: { label: string; value: string| number; }[] =[];
    const half_length = Math.ceil(enumvalues.length / 2);
    const labels = enumvalues.slice(0,half_length) as string[];
    const values = enumvalues.slice(half_length, enumvalues.length);
    labels.forEach((el, index)=> {
        resp.push({
            label: el,
            value: values[index]
        })
    });
    return resp;
}

export {
    emailIsValid,
    passwordAreEqual,
    phoneNumberIsValid,
    zipCodeIsValid,
    passwordIsValid,
    dateIsValid,
    urlIsValid,
    getStates,
    getCountries,
    getAvailableCountries,
    endDateIsValid,
    amountIsValid,
    formatPhoneNumber,
    getUserRoles,
    formatLocation,
    formatDate,
    formatDateOnly,
    getCookie,
    setCookie,
    useDebounse,
    useWindowDimensions,
    exportToCSV,
    FormatDateInputValue,
    getListFromEnum
};
